import type { AxiosResponse } from "axios";

import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// =============================================================================
// Quotation Phase C — task-bound document downloads (svc-06, /v1/tasks/...).
// Mirrors packages/shared-types/src/svc-06-quotation/v1/document.schema.ts.
//
//   Cost Analysis (.xlsx)  gate canGenerateRoi   — STREAMED
//   ROI Report    (.pdf)   gate canGenerateRoi   — STREAMED
//   Quotation     (.pdf)   gate canGenerateQuote — PERSISTS to S3 (svc-07 gated)
//   Word          (.docx)  gate canGenerateQuote — PERSISTS to S3 (svc-07 gated)
//
// Money/qty travel as Decimal-precision strings (rule §D.12).
// =============================================================================

export type DocumentFormat = "pdf" | "docx";
export type DocumentLanguage = "en" | "de";

export interface DocAssumptions {
  laborRate?: string;
  daysPerYear?: string;
  hoursPerDay?: string;
  scrapManual?: string;
  scrapAuto?: string;
  reworkCost?: string;
  oeeManual?: string;
  oeeAuto?: string;
  usefulLifeYears?: string;
}

export interface CostAnalysisTaskRequest {
  compareMachineTypes?: string[];
  assumptions?: DocAssumptions;
}

export interface RoiReportTaskRequest {
  baselineMachineType?: string | null;
  assumptions?: DocAssumptions;
}

export interface QuoteRenderRequest {
  format?: DocumentFormat;
  language?: DocumentLanguage;
  includeRoi?: boolean;
}

export interface QuoteFileResponse {
  fileId: string;
  taskId: string;
  format: DocumentFormat;
  language: string;
  includeRoi: boolean;
  downloadUrl: string;
  expiresAt: string;
  sizeBytes: number | null;
  pdfEngine: string | null;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Browser download helpers
// ---------------------------------------------------------------------------

function filenameFromDisposition(
  disposition: string | undefined,
  fallback: string,
): string {
  if (!disposition) return fallback;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, ""));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  if (plain?.[1]) return plain[1];
  return fallback;
}

function saveBlobResponse(
  res: AxiosResponse<Blob>,
  fallbackName: string,
): void {
  const disposition =
    (res.headers?.["content-disposition"] as string | undefined) ?? undefined;
  const fileName = filenameFromDisposition(disposition, fallbackName);
  triggerBrowserDownload(res.data, fileName);
}

export function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function openSignedUrl(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

// ---------------------------------------------------------------------------
// Streamed downloads (task-bound)
// ---------------------------------------------------------------------------

export async function downloadCostAnalysisForTask(
  taskId: string,
  body: CostAnalysisTaskRequest = {},
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/cost-analysis`,
    body,
    { responseType: "blob" },
  );
  saveBlobResponse(res, `cost-analysis-${taskId}.xlsx`);
}

export async function downloadRoiReportForTask(
  taskId: string,
  body: RoiReportTaskRequest = {},
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/roi-report`,
    body,
    { responseType: "blob" },
  );
  saveBlobResponse(res, `roi-report-${taskId}.pdf`);
}

export async function downloadSupportResponseForTask(
  taskId: string,
  format: DocumentFormat,
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/support-response/render`,
    { format },
    { responseType: "blob" },
  );
  saveBlobResponse(res, `support-response-${taskId}.${format}`);
}

// ---------------------------------------------------------------------------
// Persisted quotation render (.pdf / .docx) — returns signed S3 URL
// ---------------------------------------------------------------------------

export async function renderQuoteForTask(
  taskId: string,
  body: QuoteRenderRequest = {},
): Promise<QuoteFileResponse> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/quote/render`,
    body,
  );
  return unwrap<QuoteFileResponse>(res.data);
}

export async function listQuoteFiles(
  taskId: string,
): Promise<QuoteFileResponse[]> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/quote/files`,
  );
  return unwrap<QuoteFileResponse[]>(res.data);
}
